// Traffic Monitoring API functions
import type {
  AirEventListResponse,
  AirEventParams,
  AssetStatusParams,
  AssetsStatusResponse,
  DetectorDtRequest,
  EventParams,
  EventResponse,
  GoboImageListResponse,
  MainDashboardInitialViewResponse,
  MainDashboardMapPoiResponse,
  MapOverviewResponse,
  ObjectCountResponse,
  RealTimeObjectsResponse,
  RestartRequest,
  SiteDetectorResponse,
  SiteOverviewParams,
  SiteOverviewResponse,
  SiteRoadParams,
  SiteRoadResponse,
  SitesOverviewResponse,
  TimeSeriesObjectUniqueCountByRoad,
  TrafficCongestionByLocationParams,
  TrafficCongestionByLocationResponse,
  TrafficCongestionParams,
  TrafficCongestionResponse,
  TrafficEventCountResponse,
  TrafficEventListTodayParams,
  TrafficEventListTodayResponse,
  TrafficEventSummaryParams,
  TrafficEventSummaryResponse,
  TrafficExportParams,
  TrafficObjectParams,
  TrafficObjectResponse,
  TrafficParams,
  TrafficSeriesResponse,
  TrafficVolumeByVehicleParams,
  TrafficVolumeByVehicleResponse,
  TrafficVolumeHourlyParams,
  TrafficVolumeHourlyResponse,
  TrafficVolumeSpeedTrendParams,
  TrafficVolumeSpeedTrendResponse,
  VehicleInOutCountParams,
  VehicleInOutCountResponse,
} from '@/domains/traffic-monitoring/types/dashboard.types';
import { APIS, URLS } from '@/shared/config/urls';
import type { ApiResponse } from '@/shared/types/abstract-api-response';
import type { SiteData } from '@/shared/types/types-site-management';
import { request } from '@/shared/utils/request';

export const dashboardApi = {
  // === Overview APIs ===

  // DSH_074 - Get Time Series Object Unique Count by Road
  getTimeSeriesObjectUniqueCount: async (
    startTime: string,
    endTime: string,
    site_id: number,
    interval: number,
    type: string
  ): Promise<ApiResponse<TimeSeriesObjectUniqueCountByRoad[]>> => {
    const params = new URLSearchParams();
    if (startTime) params.append('start_time', startTime);
    if (endTime) params.append('end_time', endTime);
    if (site_id) params.append('site_id', site_id as unknown as string);
    if (interval) params.append('interval', interval as unknown as string);
    if (type) params.append('type', type);

    const queryString = params.toString();
    const url = queryString
      ? `/object-unq-cnt/road/time-series?${queryString}`
      : '/object-unq-cnt/road/time-series';

    return request.get<ApiResponse<TimeSeriesObjectUniqueCountByRoad[]>>(url);
  },

  // DSH_101 - Get Map Overview
  getMapOverview: async (): Promise<MapOverviewResponse> => {
    return request.get<MapOverviewResponse>('/overview/map');
  },

  // DSH_102 - Get Sites Overview
  getSitesOverview: async (params: SiteOverviewParams): Promise<SitesOverviewResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.overviewSite(queryParams.toString())}`;
    return request.get(url);
  },

  // DSH_103 - Get Site Overview
  getSiteOverview: async (siteId: number): Promise<SiteOverviewResponse> => {
    return request.get<SiteOverviewResponse>(`/overview/site/${siteId}`);
  },

  // DSH_104 - Get Assets Status
  getAssetsStatus: async (params: AssetStatusParams): Promise<AssetsStatusResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.overviewAsset(queryParams.toString())}`;
    return request.get(url);
  },

  // DSH_001 - Get Site item (detail record). Replaces the `/site` websocket
  // event: same get_site_item service; datetime fields are serialized to
  // strings by the backend JSON encoder, matching the old ws payload.
  getSite: async (siteId: number): Promise<ApiResponse<SiteData>> => {
    return request.get<ApiResponse<SiteData>>(`/site/${siteId}`);
  },

  // DSH_021 - Get every facility for a site. Replaces the `/facility` websocket
  // event: size=0 returns all rows (no pagination), as the ws handler did.
  getFacilities: async (siteId: number): Promise<ApiResponse<{ rows: unknown[] }>> => {
    const url = `${URLS.BACK_DSH}${APIS.facilityList(`site_id=${siteId}&size=0`)}`;
    return request.get(url);
  },

  // === Site Detail APIs ===

  // DSH_105 - Get Object Count
  getObjectCount: async (siteId: number, roadId?: number): Promise<ObjectCountResponse> => {
    const params = new URLSearchParams();
    if (roadId) params.append('road_id', roadId.toString());

    const queryString = params.toString();
    const url = queryString
      ? `/overview/object-count/${siteId}?${queryString}`
      : `/overview/object-count/${siteId}`;

    return request.get<ObjectCountResponse>(url);
  },

  // DSH_106 - Get Real-time Objects
  getRealTimeObjects: async (siteId: number, roadId?: number): Promise<RealTimeObjectsResponse> => {
    const params = new URLSearchParams();
    if (roadId) params.append('road_id', roadId.toString());

    const queryString = params.toString();
    const url = queryString
      ? `/overview/real-time-objects/${siteId}?${queryString}`
      : `/overview/real-time-objects/${siteId}`;

    return request.get<RealTimeObjectsResponse>(url);
  },

  // DSH_039 - Get Traffic Events
  getTrafficEvents: async <T = EventResponse>(params: EventParams): Promise<ApiResponse<T>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) return;

      if (Array.isArray(value)) {
        value.forEach((v) => {
          queryParams.append(key, v);
        });
      } else {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficEventTime(queryParams.toString())}`;
    return request.get(url);
  },

  // DSH_029 - Get site road mapped to detector
  getSiteRoadDetector: async (siteId: number): Promise<ApiResponse<SiteDetectorResponse>> => {
    const url = `${URLS.BACK_DSH}${APIS.siteRoadDetector(siteId)}`;
    return request.get(url);
  },

  // DSH_043
  getTotalObject: async (
    params: TrafficObjectParams
  ): Promise<ApiResponse<TrafficObjectResponse>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.objectUnqCnt(queryParams.toString())}`;
    return request.get(url);
  },

  // DSH_087
  getTrafficSeries: async (
    params: TrafficParams
  ): Promise<ApiResponse<TrafficSeriesResponse[]>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficSeries(queryParams.toString())}`;
    return request.get(url);
  },

  getTrafficEventCount: async (
    params: TrafficParams
  ): Promise<ApiResponse<TrafficEventCountResponse[]>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficEventCount(queryParams.toString())}`;
    return request.get(url);
  },
  // === Dashboard Specific APIs ===

  // restart facility
  boxCommand: async (id: number, data: RestartRequest): Promise<ApiResponse<null>> => {
    const url = `${URLS.BACK_ENV}${APIS.boxCommand(id)}`;
    return request.put(url, data);
  },

  updateDetectorDt: async (id: number, data: DetectorDtRequest): Promise<ApiResponse<null>> => {
    const url = `${URLS.BACK_DSH}${APIS.updateDetectorDt(id)}`;
    return request.put(url, data);
  },

  //DSH_106 - Get Gobo Image List
  getGoboImageList: async (): Promise<GoboImageListResponse> => {
    const url = `${URLS.BACK_DSH}${APIS.goboImageList}`;
    return request.get<GoboImageListResponse>(url);
  },

  // Main dashboard
  getMainDashboardMapPoi: async (): Promise<MainDashboardMapPoiResponse> => {
    const url = `${URLS.BACK_DSH}${APIS.mapDisplayPOI}`;
    return request.get(url);
  },

  getMainDashboardMapInitialView: async (): Promise<MainDashboardInitialViewResponse> => {
    const url = `${URLS.BACK_DSH}${APIS.mapInitialView}`;
    return request.get(url);
  },

  getAirEventList: async (params: AirEventParams): Promise<AirEventListResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) return;

      if (Array.isArray(value)) {
        value.forEach((v) => {
          queryParams.append(key, v);
        });
      } else {
        queryParams.append(key, value.toString());
      }
    });
    const url = `${URLS.BACK_DSH}${APIS.airEventList(queryParams.toString())}`;
    return request.get(url);
  },

  // PSC_PC0203001_BE_001 - Today's Traffic Volume Grouped by Vehicle
  getTrafficVolumeByVehicle: async (
    params: TrafficVolumeByVehicleParams
  ): Promise<TrafficVolumeByVehicleResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficVolumeByVehicle(queryParams.toString())}`;
    return request.get(url);
  },

  // PSC_PC0203001_BE_006 - Traffic Event Counts by Type (Today + Day-over-Day Change)
  getTrafficEventSummary: async (
    params: TrafficEventSummaryParams
  ): Promise<TrafficEventSummaryResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficEventSummary(queryParams.toString())}`;
    return request.get(url);
  },

  // PSC_PC0203001_BE_007 - Traffic Event List (from start_time up to now)
  getTrafficEventListToday: async (
    params: TrafficEventListTodayParams
  ): Promise<TrafficEventListTodayResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficEventListToday(queryParams.toString())}`;
    return request.get(url);
  },

  // PSC_PC0203001_BE_004 - Vehicle In/Out Count (+ per-direction avg speed)
  getVehicleInOutCount: async (
    params: VehicleInOutCountParams
  ): Promise<VehicleInOutCountResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) return;
      // cctv_id is repeatable; omit it entirely for the area-wide total.
      if (Array.isArray(value)) {
        value.forEach((v) => {
          queryParams.append(key, v.toString());
        });
      } else {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.vehicleInOutCount(queryParams.toString())}`;
    return request.get(url);
  },

  // PSC_PC0203001_BE_003 - Traffic Congestion Summary (Today)
  getTrafficCongestion: async (
    params: TrafficCongestionParams
  ): Promise<TrafficCongestionResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficCongestion(queryParams.toString())}`;
    return request.get(url);
  },

  // Traffic Congestion by Location (Today) - per-CCTV heatmap feed
  getTrafficCongestionByLocation: async (
    params: TrafficCongestionByLocationParams
  ): Promise<TrafficCongestionByLocationResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficCongestionByLocation(queryParams.toString())}`;
    return request.get(url);
  },

  // PSC_PC0203001_BE_005 - Traffic Volume & Average-Speed Hourly Trend (24h)
  getTrafficVolumeSpeedTrend: async (
    params: TrafficVolumeSpeedTrendParams
  ): Promise<TrafficVolumeSpeedTrendResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficVolumeSpeedTrend(queryParams.toString())}`;
    return request.get(url);
  },

  // PSC_PC0203001 - Hourly Traffic Volume by Vehicle (peak-hours trend)
  getTrafficVolumeHourlyByVehicle: async (
    params: TrafficVolumeHourlyParams
  ): Promise<TrafficVolumeHourlyResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.trafficVolumeHourlyByVehicle(queryParams.toString())}`;
    return request.get(url);
  },

  getSiteRoad: async (params: SiteRoadParams): Promise<SiteRoadResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) return;

      if (Array.isArray(value)) {
        value.forEach((v) => {
          queryParams.append(key, v);
        });
      } else {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${URLS.BACK_DSH}${APIS.siteRoad(queryParams.toString())}`;
    return await request.get(url);
  },

  // PSC_PC0203001_BE_008 - Traffic Volume by Vehicle, as an .xlsx attachment.
  exportTrafficVolume: async (params: TrafficExportParams): Promise<Blob> => {
    const queryParams = new URLSearchParams({ ...params });
    const url = `${URLS.BACK_DSH}${APIS.trafficVolumeExport(queryParams.toString())}`;
    return request.get(url, { responseType: 'blob' });
  },

  // PSC_PC0203001_BE_009 - Traffic Event counts, as an .xlsx attachment.
  exportTrafficEvent: async (params: TrafficExportParams): Promise<Blob> => {
    const queryParams = new URLSearchParams({ ...params });
    const url = `${URLS.BACK_DSH}${APIS.trafficEventExport(queryParams.toString())}`;
    return request.get(url, { responseType: 'blob' });
  },

  // PSC_PC0204002_BE_001 - One camera's vehicle + event counts, as an .xlsx
  // attachment. `cctv_id` is required here; the endpoint 400s without it.
  exportTrafficCctv: async (params: TrafficExportParams): Promise<Blob> => {
    const queryParams = new URLSearchParams({ ...params });
    const url = `${URLS.BACK_DSH}${APIS.trafficCctvExport(queryParams.toString())}`;
    return request.get(url, { responseType: 'blob' });
  },
};
